// SideNavBarLayout.tsx
import { Drawer, List, ListItemText, AppBar, Menu, MenuItem, Toolbar, Typography, Box, CssBaseline, useTheme, IconButton, useMediaQuery, ListItemIcon, ListItemButton, Avatar, Divider, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import routes from './app-routes';
import { styled } from '@mui/system';
import MenuIcon from '@mui/icons-material/Menu';
import SpaceDashboardOutlined from '@mui/icons-material/SpaceDashboardOutlined';
import HandymanOutlined from '@mui/icons-material/HandymanOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import CreditScoreOutlined from '@mui/icons-material/CreditScoreOutlined';
import CategoryOutlined from '@mui/icons-material/CategoryOutlined';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import TrendingUpOutlined from '@mui/icons-material/TrendingUpOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import AssignmentTurnedInOutlined from '@mui/icons-material/AssignmentTurnedInOutlined';
import RequestQuoteOutlined from '@mui/icons-material/RequestQuoteOutlined';
import FiberManualRecordOutlined from '@mui/icons-material/FiberManualRecordOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './context/auth';

const drawerWidth = 260;

/* Leyenda de cada modulo para la barra superior. Solo presentacion: el nombre
   del modulo se toma de app-routes, aqui solo vive la descripcion corta. */
const routeCaptions: Record<string, string> = {
  '/home': 'Resumen general del sistema',
  '/servicios': 'Registro y seguimiento de instalaciones',
  '/inventario': 'Stock de productos y accesorios',
  '/liquidaciones': 'Pagos y cierres de instaladores',
  '/Creditos': 'Saldos y abonos de clientes',
  '/Materiales': 'Consumo por instalador e inventario global',
  '/OrdenesCompra': 'Solicitudes a proveedores',
  '/ResumenGanancias': 'Rentabilidad por período',
  '/Configuracion': 'Maestros, usuarios y permisos',
  '/MisServicios': 'Servicios asignados a tu usuario',
  '/Facturacion': 'Preparación y control de facturas',
};

/* Icono decorativo por ruta. Solo presentacion: no altera el enrutamiento. */
const routeIcons: Record<string, JSX.Element> = {
  '/home': <SpaceDashboardOutlined fontSize='small' />,
  '/servicios': <HandymanOutlined fontSize='small' />,
  '/inventario': <Inventory2Outlined fontSize='small' />,
  '/liquidaciones': <ReceiptLongOutlined fontSize='small' />,
  '/Creditos': <CreditScoreOutlined fontSize='small' />,
  '/Materiales': <CategoryOutlined fontSize='small' />,
  '/OrdenesCompra': <ShoppingCartOutlined fontSize='small' />,
  '/ResumenGanancias': <TrendingUpOutlined fontSize='small' />,
  '/Configuracion': <TuneOutlined fontSize='small' />,
  '/MisServicios': <AssignmentTurnedInOutlined fontSize='small' />,
  '/Facturacion': <RequestQuoteOutlined fontSize='small' />,
};

const StyledDrawer = styled(Drawer)(() => ({
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    backgroundColor: 'rgba(255, 255, 255, 0.62)',
    backdropFilter: 'blur(22px) saturate(180%)',
    WebkitBackdropFilter: 'blur(22px) saturate(180%)',
    borderRight: '1px solid rgba(18, 48, 58, 0.08)',
    boxShadow: '10px 0 40px rgba(16, 58, 74, 0.05)',
    color: '#12303A',
    overflowX: 'hidden',
  },
}));

const SideNavBarLayout: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  const location = useLocation();
  const { user, signOut, permisos } = useAuth();

  // Titulo de la barra superior: el modulo activo, no la marca.
  const currentRoute = routes.find((route) => route.path.toLowerCase() === location.pathname.toLowerCase());
  const moduleTitle = currentRoute?.name ?? 'SafetySound';
  const moduleCaption = (currentRoute && routeCaptions[currentRoute.path]) ?? 'Accesorios automotrices';

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseSession = () => {
    setAnchorEl(null);
    signOut();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  return (
    <Box>
      <CssBaseline />
      <AppBar
        position='fixed'
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 100,
          background: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(22px) saturate(180%)',
          WebkitBackdropFilter: 'blur(22px) saturate(180%)',
          color: 'text.primary',
          borderBottom: '1px solid rgba(18, 48, 58, 0.08)',
          boxShadow: 'none',
          width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
          ml: isMobile ? 0 : `${drawerWidth}px`,
        }}>
        <Toolbar sx={{ gap: 1 }}>
          {isMobile && (
            <IconButton
              color='inherit'
              aria-label='open drawer'
              edge='start'
              onClick={handleDrawerToggle}
              sx={{
                mr: 1,
                borderRadius: '12px',
                border: '1px solid rgba(18, 48, 58, 0.1)',
                background: 'rgba(255,255,255,0.6)',
              }}>
              <MenuIcon fontSize='small' />
            </IconButton>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1, minWidth: 0 }}>
            <Typography
              variant='h6'
              noWrap
              component='div'
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.02em',
                background: 'linear-gradient(120deg, #0A7A6E 0%, #0E9F8F 45%, #7C6CF0 110%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
              {moduleTitle}
            </Typography>
            {!isMobile && (
              <Typography
                variant='caption'
                noWrap
                sx={{ color: 'text.secondary', fontWeight: 500, letterSpacing: '0.02em', fontSize: '0.68rem' }}>
                {moduleCaption}
              </Typography>
            )}
          </Box>

          {/* Agregar esto al final de la barra de herramientas */}
          <Box sx={{ flexGrow: 1 }} />
          {!isMobile && (
            <Box sx={{ textAlign: 'right', mr: 0.5, lineHeight: 1.15 }}>
              <Typography variant='inherit' noWrap component='div' sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary' }}>
                {user?.Us_Nombre}
              </Typography>
              <Typography variant='caption' noWrap sx={{ color: 'text.secondary', fontSize: '0.66rem' }}>
                Sesión activa
              </Typography>
            </Box>
          )}
          <Tooltip title='Mi cuenta'>
            <IconButton onClick={handleClick} sx={{ p: 0.5, borderRadius: '14px' }}>
              <Avatar>{user?.Us_Nombre[0]}</Avatar>
            </IconButton>
          </Tooltip>
          <Menu id='simple-menu' anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose} sx={{ mt: 1 }}>
            {/* <MenuItem onClick={handleClose}>Mi cuenta</MenuItem> */}
            <MenuItem onClick={handleCloseSession}>
              <ListItemIcon sx={{ minWidth: 30 }}>
                <LogoutOutlined fontSize='small' />
              </ListItemIcon>
              Cerrar sesión
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <StyledDrawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={open}
        onClose={handleDrawerToggle}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { boxSizing: 'border-box', width: drawerWidth },
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pt: isMobile ? '52px' : '12px',
            pb: '8px',
            px: 2,
          }}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              borderRadius: '14px',
              background: 'linear-gradient(150deg, rgba(255,255,255,0.9), rgba(255,255,255,0.35))',
              border: '1px solid rgba(255,255,255,0.75)',
              boxShadow: '0 6px 18px rgba(16, 58, 74, 0.05)',
              py: 0.75,
            }}>
            <img src={'images/fondoWhite.png'} alt='logo' style={{ height: '38px', objectFit: 'contain' }} />
          </Box>
        </Box>

        <Typography
          variant='caption'
          sx={{
            px: 3,
            pt: 0.5,
            pb: 0.25,
            color: 'text.disabled',
            fontWeight: 700,
            fontSize: '0.58rem',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
          }}>
          Navegación
        </Typography>

        <List sx={{ px: 1.5, py: 0.5, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {routes.map((route) => {
            if (permisos[route.permission] === 0) return null;

            const active = location.pathname === route.path;

            return (
              <ListItemButton
                key={route.path}
                component={Link}
                to={route.path}
                disableRipple={false}
                sx={{
                  position: 'relative',
                  borderRadius: '14px',
                  py: 0.9,
                  pl: 1.75,
                  pr: 1.25,
                  color: active ? 'primary.dark' : 'text.secondary',
                  background: active ? 'linear-gradient(120deg, rgba(14,159,143,0.16), rgba(124,108,240,0.10))' : 'transparent',
                  border: active ? '1px solid rgba(14,159,143,0.22)' : '1px solid transparent',
                  boxShadow: active ? '0 6px 18px rgba(14,159,143,0.14)' : 'none',
                  transition: 'all .2s ease',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 4,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '3px',
                    height: active ? '18px' : '0px',
                    borderRadius: '999px',
                    background: 'linear-gradient(180deg, #5FD6C6, #0E9F8F)',
                    transition: 'height .2s ease',
                  },
                  '&:hover': {
                    background: active ? 'linear-gradient(120deg, rgba(14,159,143,0.2), rgba(124,108,240,0.12))' : 'rgba(255,255,255,0.7)',
                    color: 'primary.dark',
                    transform: 'translateX(2px)',
                  },
                }}>
                <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}>{routeIcons[route.path] ?? <FiberManualRecordOutlined sx={{ fontSize: 12 }} />}</ListItemIcon>
                <ListItemText primary={route.name} primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: active ? 700 : 600, noWrap: true }} />
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ flexGrow: 1 }} />
        <Divider sx={{ mx: 2, borderColor: 'rgba(18,48,58,0.07)' }} />
        <Typography variant='caption' sx={{ px: 3, py: 1.5, color: 'text.disabled', fontSize: '0.62rem', letterSpacing: '0.04em' }}>
          Safety Sound · Panel de gestión
        </Typography>
      </StyledDrawer>
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          px: { xs: 1.5, sm: 3 },
          pt: 2,
          pb: 4,
          ml: isMobile ? 0 : `${drawerWidth}px`,
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
        }}>
        {children}
      </Box>
    </Box>
  );
};

export default SideNavBarLayout;
